import React, { useCallback, useEffect, useMemo } from 'react';
import { Collapse, Button, Space, Grid, Message } from '@arco-design/web-react';
import { t, primeListings } from '@bay-email/core';
import {
	useFocusIdx,
	useEditorContext,
	useEditorProps,
	TextStyle,
	Tooltip,
} from '@bay-email/editor';
import { AttributesPanelWrapper } from '../../attributes/AttributesPanelWrapper';
import {
	NumberField,
	TextField,
	ColorPickerField,
	SwitchField,
	SelectField,
	InputWithUnitField,
} from '../../../../components/Form';
import { useSelectedNode } from '../../../../hooks/useSelectedNode';
import { useExtensionProps } from '../../../../components/Providers/ExtensionProvider';
import { FontFamily } from '../../attributes/FontFamily';
import { Link } from '../../attributes/Link';
import { MergeTags } from '../../attributes/MergeTags';
import { useField } from 'react-final-form';
import { v4 as uuidv4 } from 'uuid';
import { pixelAdapter } from '../../adapter';

import { CriteriaDrawer } from '../Listing/CriteriaDrawer';
import { CriteriaState } from '../Listing/CriteriaState';
import { countListingsByCriteria } from '../../../utils/listingsTransport';
import {
	ATTACHED_LISTING_PICKER_EVENT,
	consumePendingListingPickerOpen,
} from '../shared/listingAttachment';
import { listingLayouts } from '@bay-email/core';
import { Padding } from '@extensions/AttributePanel/components/attributes/Padding';
import { BsChevronDown } from 'react-icons/bs';

interface ListingItem {
	id: string;
	title?: string;
	address?: string;
	city?: string;
	stateProvince?: string;
	postalCode?: string;
	list_price?: number;
	beds?: number;
	baths?: number;
	sq_foot?: number;
	image_data?: string[];
}

export interface CriteriaBlockPanelProps {
	nodePath?: string;
}

export const CriteriaBlock: React.FC<CriteriaBlockPanelProps> = ({ nodePath }) => {
	const [criteriaDrawerVisible, setCriteriaDrawerVisible] =
		React.useState<boolean>(false);
	const [activeKeys, setActiveKeys] = React.useState<string[]>(['1', '2', '3', '4']);
	const [criteriaConfig, setCriteriaConfig] = React.useState<any>({});
	const [criteriaResults, setCriteriaResults] = React.useState<ListingItem[]>([]);
	const [criteriaResultCount, setCriteriaResultCount] = React.useState<number>(0);
	const [loading, setLoading] = React.useState<boolean>(false);

	const { selectedNode } = useSelectedNode();
	const { focusIdx } = useFocusIdx();
	const { formHelpers } = useEditorContext();
	const { isDesignAdmin } = useExtensionProps();
	const { mergeTags } = useEditorProps();

	// Get button text field for merge tags
	const { input: buttonTextInput } = useField(`${focusIdx}.attributes.button-text`, {
		parse: v => v,
	});

	const { input: showAddressInput } = useField(`${focusIdx}.attributes.show-address`);
	const { input: showPriceInput } = useField(`${focusIdx}.attributes.show-price`);
	const { input: showDetailsInput } = useField(`${focusIdx}.attributes.show-details`);

	// Generate layout options from mock data
	const layoutOptions = useMemo(() => {
		return listingLayouts.map((layout: any) => ({
			label: t(layout.name),
			value: layout.id,
		}));
	}, []);

	// Sort rules for the listings returned by the criteria
	const sortOptions = useMemo(
		() => [
			{ label: t('Price (High to Low)'), value: 'price_desc' },
			{ label: t('Price (Low to High)'), value: 'price_asc' },
			{ label: t('Date Listed (Newest to Oldest)'), value: 'date_desc' },
			{ label: t('Date Listed (Oldest to Newest)'), value: 'date_asc' },
		],
		[],
	);

	// Sync criteria data with block data
	React.useEffect(() => {
		const blockCriteriaConfig = selectedNode?.data?.value?.criteriaConfig || {};
		const blockCriteriaResults = selectedNode?.data?.value?.criteriaResults || [];
		const blockResultCount = selectedNode?.data?.value?.criteriaResultCount || 0;

		setCriteriaConfig(blockCriteriaConfig);
		setCriteriaResults(blockCriteriaResults);
		// Legacy blocks only: save clears this field, so a block with the drawer's
		// config gets its count live below instead (monday 13121451389).
		if (!selectedNode?.data?.value?.originalConfig) setCriteriaResultCount(blockResultCount);
	}, [
		selectedNode?.data?.value?.criteriaConfig,
		selectedNode?.data?.value?.criteriaResults,
		selectedNode?.data?.value?.criteriaResultCount,
		selectedNode?.data?.value?.originalConfig,
	]);

	// Results Found = the drawer's live count for the saved criteria.
	const originalConfig = selectedNode?.data?.value?.originalConfig;
	React.useEffect(() => {
		if (!originalConfig) return;
		let current = true;
		// Clear the previous block's count until this one lands.
		setCriteriaResultCount(0);
		countListingsByCriteria(originalConfig)
			.then(count => current && setCriteriaResultCount(count))
			.catch(() => current && setCriteriaResultCount(0));
		return () => {
			current = false;
		};
	}, [originalConfig]);

	// Initialize show toggles, font sizes, and maxCount to defaults if undefined
	React.useEffect(() => {
		if (focusIdx && formHelpers) {
			const showAddress = formHelpers.getFieldState(
				`${focusIdx}.attributes.show-address`,
			)?.value;
			const showPrice = formHelpers.getFieldState(
				`${focusIdx}.attributes.show-price`,
			)?.value;
			const showDetails = formHelpers.getFieldState(
				`${focusIdx}.attributes.show-details`,
			)?.value;
			const titleFontSize = formHelpers.getFieldState(
				`${focusIdx}.attributes.title-font-size`,
			)?.value;
			const priceFontSize = formHelpers.getFieldState(
				`${focusIdx}.attributes.price-font-size`,
			)?.value;
			const addressFontSize = formHelpers.getFieldState(
				`${focusIdx}.attributes.address-font-size`,
			)?.value;
			const buttonFontSize = formHelpers.getFieldState(
				`${focusIdx}.attributes.button-font-size`,
			)?.value;
			const maxCount = formHelpers.getFieldState(
				`${focusIdx}.data.value.maxCount`,
			)?.value;
			const sortBy = formHelpers.getFieldState(
				`${focusIdx}.data.value.sortBy`,
			)?.value;

			if (showAddress === undefined) {
				formHelpers.change(`${focusIdx}.attributes.show-address`, true);
			}
			if (showPrice === undefined) {
				formHelpers.change(`${focusIdx}.attributes.show-price`, true);
			}
			if (showDetails === undefined) {
				formHelpers.change(`${focusIdx}.attributes.show-details`, true);
			}
			if (titleFontSize === undefined) {
				formHelpers.change(`${focusIdx}.attributes.title-font-size`, '16px');
			}
			if (priceFontSize === undefined) {
				formHelpers.change(`${focusIdx}.attributes.price-font-size`, '18px');
			}
			if (addressFontSize === undefined) {
				formHelpers.change(`${focusIdx}.attributes.address-font-size`, '14px');
			}
			if (buttonFontSize === undefined) {
				formHelpers.change(`${focusIdx}.attributes.button-font-size`, '14px');
			}
			if (maxCount === undefined) {
				formHelpers.change(`${focusIdx}.data.value.maxCount`, 4);
			}
			if (sortBy === undefined) {
				formHelpers.change(`${focusIdx}.data.value.sortBy`, 'price_desc');
			}
		}
	}, [focusIdx, formHelpers]);

	// Open the drawer when the canvas toolbar's "Select Listing" shortcut fires
	// for this block. The dispatch can land before this panel mounts (focus
	// moves, then the panel mounts after) — consume the sticky request on
	// mount, and also listen live for the already-mounted case.
	React.useEffect(() => {
		// Design-admin never gets the toolbar shortcut, so never open from it here.
		if (!isDesignAdmin && consumePendingListingPickerOpen(focusIdx)) {
			setCriteriaDrawerVisible(true);
		}
		if (typeof window === 'undefined') return;
		const handleOpen = (event: Event) => {
			const detail = (event as CustomEvent<{ idx?: string }>).detail;
			if (detail?.idx === focusIdx && !isDesignAdmin) {
				// Clear the sticky request the dispatch also set, so a later remount of
				// this focusIdx doesn't consume it and reopen the drawer unprompted.
				consumePendingListingPickerOpen(focusIdx);
				setCriteriaDrawerVisible(true);
			}
		};
		window.addEventListener(ATTACHED_LISTING_PICKER_EVENT, handleOpen as EventListener);
		return () =>
			window.removeEventListener(ATTACHED_LISTING_PICKER_EVENT, handleOpen as EventListener);
	}, [focusIdx, isDesignAdmin]);

	// Transform form data to match CriteriaState expected format
	// Helper to parse and validate numeric values - only return if different from defaults
	const parseNumericValue = (value: any, defaultValue: number): number | undefined => {
		if (value === undefined || value === null || value === '') return undefined;
		// Handle arrays - if it's an array, take the first element
		if (Array.isArray(value)) {
			value = value[0];
		}
		const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
		if (isNaN(parsed) || parsed === defaultValue) return undefined;
		return parsed;
	};

	const transformCriteriaConfig = (formConfig: any) => {
		return {
			...formConfig,
			// Map lowercase/snake_case to camelCase for CriteriaState
			// Only pass values that differ from defaults
			propertyType: formConfig.propertytype || formConfig.propertyType,
			priceMin: parseNumericValue(
				formConfig.pricemin !== undefined ? formConfig.pricemin : formConfig.priceMin,
				0,
			),
			priceMax: parseNumericValue(
				formConfig.pricemax !== undefined ? formConfig.pricemax : formConfig.priceMax,
				99999999,
			),
			sqftMin: parseNumericValue(
				formConfig.sq_footmin !== undefined ? formConfig.sq_footmin : formConfig.sqftMin,
				0,
			),
			sqftMax: parseNumericValue(
				formConfig.sq_footmax !== undefined ? formConfig.sq_footmax : formConfig.sqftMax,
				99999,
			),
			bedsMin: parseNumericValue(
				formConfig.bedsmin !== undefined ? formConfig.bedsmin : formConfig.bedsMin,
				0,
			),
			bedsMax: parseNumericValue(
				formConfig.bedsmax !== undefined ? formConfig.bedsmax : formConfig.bedsMax,
				99,
			),
			bathsMin: parseNumericValue(
				formConfig.bathsmin !== undefined ? formConfig.bathsmin : formConfig.bathsMin,
				0,
			),
			bathsMax: parseNumericValue(
				formConfig.bathsmax !== undefined ? formConfig.bathsmax : formConfig.bathsMax,
				99,
			),
			unitsMin: parseNumericValue(
				formConfig.unitsmin !== undefined ? formConfig.unitsmin : formConfig.unitsMin,
				0,
			),
			unitsMax: parseNumericValue(
				formConfig.unitsmax !== undefined ? formConfig.unitsmax : formConfig.unitsMax,
				99,
			),
			buildingsMin: parseNumericValue(
				formConfig.buildingsmin !== undefined
					? formConfig.buildingsmin
					: formConfig.buildingsMin,
				0,
			),
			buildingsMax: parseNumericValue(
				formConfig.buildingsmax !== undefined
					? formConfig.buildingsmax
					: formConfig.buildingsMax,
				99,
			),
			acreageMin:
				formConfig.acreagemin !== undefined
					? formConfig.acreagemin
					: formConfig.acreageMin,
			acreageMax:
				formConfig.acreagemax !== undefined
					? formConfig.acreagemax
					: formConfig.acreageMax,
			yearBuilt: formConfig.year_built || formConfig.yearBuilt,
			officeSqftMin:
				formConfig.sq_footofficemin !== undefined
					? formConfig.sq_footofficemin
					: formConfig.officeSqftMin,
			officeSqftMax:
				formConfig.sq_footofficemax !== undefined
					? formConfig.sq_footofficemax
					: formConfig.officeSqftMax,
			propertyStyles: formConfig.style
				? typeof formConfig.style === 'string'
					? formConfig.style.split(',').filter((s: string) => s)
					: formConfig.style
				: formConfig.propertyStyles,
			golfCourse:
				formConfig.golfcourse === 'yes' ||
				formConfig.golfcourse === 'y' ||
				formConfig.golfCourse === true ||
				formConfig.golfCourse,
			waterFront:
				formConfig.waterfront === 'yes' ||
				formConfig.waterfront === 'y' ||
				formConfig.waterFront === true ||
				formConfig.waterFront,
			propertyView:
				formConfig.propertyview === 'yes' ||
				formConfig.propertyview === 'y' ||
				formConfig.propertyView === true ||
				formConfig.propertyView,
			// Handle search mode
			criteriaType:
				formConfig.searchby === 'map'
					? 'map'
					: formConfig.searchby === 'list'
						? 'area'
						: formConfig.criteriaType,
			// Map coordinates
			lat: formConfig.lat,
			lng: formConfig.lon || formConfig.lng,
			zoomLevel: formConfig.zoomlevel || formConfig.zoomLevel,
			// Location fields (keep both formats for compatibility)
			city: formConfig.city,
			county: formConfig.county,
			state: formConfig.stateProvince || formConfig.state,
			postalCode: formConfig.postalCode,
			area: formConfig.area,
			subarea: formConfig.subarea,
		};
	};

	// Handle criteria save
	const handleCriteriaSave = useCallback(
		async (config: any, maxListings: number, providedCount?: number) => {
			setLoading(true);

			// Transform the config to match expected format
			const transformedConfig = transformCriteriaConfig(config);
			setCriteriaConfig(transformedConfig);

			try {
				// Use the count that was already fetched in the drawer
				// No need to fetch listings again - we already have the count
				const total = providedCount || 0;

				// Update the filtered listings to display in the editor for CBAY only.
				const results: ListingItem[] = criteriaResults || [];
				setCriteriaResultCount(total);

				if (focusIdx) {
					// A criteria block has no natural id of its own (unlike Single/
					// Multiple/Custom, which bind to an MLS composite id) - two blocks
					// can easily end up with byte-identical criteriaConfig, so this is
					// generated once here, lazily, the same way Image's
					// ensureDynamicImageId only generates on first use rather than at
					// block creation - eager generation in create() would let a
					// duplicate-block action's cloned payload override the fresh
					// default via merge(), leaving both copies sharing one id.
					const existingCriteriaBlockId = formHelpers.getFieldState(
						`${focusIdx}.data.value.criteriaBlockId`,
					)?.value;
					if (!existingCriteriaBlockId) {
						formHelpers.change(
							`${focusIdx}.data.value.criteriaBlockId`,
							`criteria_${uuidv4().replace(/-/g, '').slice(0, 12)}`,
						);
					}

					// IMPORTANT: Save the ORIGINAL config (snake_case, string values) for the form to use when reopening
					// Also save the transformed config (camelCase, parsed numbers) for CriteriaState display
					formHelpers.change(`${focusIdx}.data.value.criteriaConfig`, transformedConfig); // For CriteriaState display
					formHelpers.change(`${focusIdx}.data.value.originalConfig`, config); // For form editing (snake_case)
					formHelpers.change(`${focusIdx}.data.value.maxCount`, maxListings);
					// The query is the binding; matching listings are not persisted.
					// A saved result set is a snapshot that keeps showing sold homes
					// and misses new ones. Seed the render cache instead, and clear
					// whatever a pre-migration save left behind.
					primeListings(results as any[]);
					formHelpers.change(`${focusIdx}.data.value.criteriaResults`, undefined);
					formHelpers.change(
						`${focusIdx}.data.value.criteriaResultCount`,
						undefined,
					);
				}
			} catch (error) {
				console.error('Error saving listings criteria:', error);
				Message.error({
					content: t('Failed to save listing criteria. Please try again.'),
					duration: 3000,
				});
			} finally {
				setLoading(false);
			}
		},
		[formHelpers, focusIdx, t, criteriaResults],
	);

	const maxListingsValue = selectedNode?.data?.value?.maxCount || 4;

	const handleCollapseAll = React.useCallback(() => {
		if (activeKeys.length > 0) {
			setActiveKeys([]);
		} else {
			setActiveKeys(['1', '2', '3', '4']);
		}
	}, [activeKeys]);

	return (
		<AttributesPanelWrapper
			onCollapseAll={handleCollapseAll}
			allCollapsed={activeKeys.length === 0}
		>
			<Collapse
				expandIconPosition='right'
				expandIcon={<BsChevronDown size='1rem' />}
				accordion={false}
				activeKey={activeKeys}
				onChange={(key, keys) => setActiveKeys(keys as string[])}
			>
				<Collapse.Item
					contentStyle={{ padding: 0 }}
					name='1'
					header={t('Listing Settings')}
				>
					<div className='px-3 py-4'>
						<CriteriaDrawer
							visible={criteriaDrawerVisible}
							onClose={() => setCriteriaDrawerVisible(false)}
							onOpen={() => setCriteriaDrawerVisible(true)}
							criteriaConfig={selectedNode?.data?.value?.originalConfig || criteriaConfig}
							onSave={handleCriteriaSave}
							maxListings={maxListingsValue}
							disabled={isDesignAdmin}
							setCriteriaResults={setCriteriaResults}
						>
							<Tooltip
								content={
									isDesignAdmin
										? t(
												"Users choosing this template will be able to select criteria to auto-fill this block's listing dynamic fields with matching listings.",
											)
										: ''
								}
								disabled={!isDesignAdmin}
							>
								<div style={{ width: '100%' }}>
									<Button
										type='primary'
										style={{
											width: '100%',
											marginBottom: Object.keys(criteriaConfig).length > 0 ? '16px' : '0',
										}}
										loading={loading}
										disabled={loading || isDesignAdmin}
									>
										{loading ? t('Searching...') : t('Select Criteria')}
									</Button>
								</div>
							</Tooltip>
						</CriteriaDrawer>

						<CriteriaState
							criteriaConfig={criteriaConfig}
							resultCount={criteriaResultCount}
							maxListings={maxListingsValue}
							loading={loading}
							isDesignAdmin={isDesignAdmin}
						/>

						<NumberField
							label={t('Max Listings Count')}
							formItem={{ className: 'm-0 mt-2' }}
							name={`${focusIdx}.data.value.maxCount`}
							min={1}
							max={24}
						/>

						<SelectField
							label={t('Sort Listings By')}
							formItem={{ className: 'm-0 mt-2' }}
							name={`${focusIdx}.data.value.sortBy`}
							className='custom-select-right-caret'
							options={sortOptions}
						/>
					</div>
				</Collapse.Item>

				<Collapse.Item
					contentStyle={{ padding: 0 }}
					name='2'
					header={t('Content Block Settings')}
				>
					<div className='p-3'>
						<SelectField
							label={t('Layout')}
							name={`${focusIdx}.attributes.layout`}
							className='custom-select-right-caret'
							options={layoutOptions}
						/>
						<Space className='my-2 d-flex justify-content-between align-items-center'>
							<span className='fw-bold text-capitalize'>{t('Button Text')}</span>
							{mergeTags && (
								<MergeTags
									showSearch={true}
									value={buttonTextInput.value}
									onChange={buttonTextInput.onChange}
									children={
										<span className='dynamicFieldDropdown text-secondary cursor-pointer d-flex gap-2'>
											<span>{`{ }`}</span>
											<span className='dynamic-field'>{t('Dynamic Fields')}</span>
										</span>
									}
									className={`h-100 rounded-end-1 rounded-0 border border-start-0  mergeTag`}
								/>
							)}
						</Space>
						<TextField
							name={`${focusIdx}.attributes.button-text`}
							formItem={{
								labelCol: {
									style: {
										display: 'none',
									},
								},
							}}
						/>
						<Link isNewDynamicField={true} />
					</div>
				</Collapse.Item>

				<Collapse.Item
					contentStyle={{ padding: 0 }}
					name='3'
					header={t('Dimension')}
				>
					<div className='d-flex flex-column gap-3 p-3 pt-4'>
						<Padding showResetAll />
					</div>
				</Collapse.Item>

				<Collapse.Item
					contentStyle={{ padding: 0 }}
					name='4'
					header={t('Styling')}
				>
					<div className='d-flex flex-column gap-3 p-3 pb-4'>
						{/* Background Color - Full Width */}
						<ColorPickerField
							formItem={{ className: 'm-0' }}
							label={t('Background Color')}
							name={`${focusIdx}.attributes.background-color`}
						/>

						<Grid.Row gutter={16}>
							{/* Listing Name Section */}
							<Grid.Col span={24}>
								<SwitchField
									label={
										<span style={{ fontWeight: 400, display: 'inline-block' }}>
											{t('Show Address')}
										</span>
									}
									name={`${focusIdx}.attributes.show-address`}
									size='small'
									formItem={{
										className: 'm-0',
										style: {
											display: 'flex',
											flexDirection: 'row',
											alignItems: 'center',
											marginBottom: '0px',
										},
										labelCol: {
											span: 0,
											style: {
												order: 2,
												paddingLeft: '12px',
												paddingRight: 0,
												width: 'auto',
											},
										},
										wrapperCol: {
											span: 0,
											style: {
												order: 1,
											},
										},
									}}
								/>
							</Grid.Col>
							{showAddressInput.value && <>
								<Grid.Col span={12} className='mb-3'>
									<ColorPickerField
										formItem={{ className: 'm-0' }}
										label={t('Listing Address Color')}
										name={`${focusIdx}.attributes.listing-name-color`}
									/>
								</Grid.Col>
								<Grid.Col span={12} className='mb-3'>
									<InputWithUnitField
										formItem={{ className: 'm-0' }}
										label={t('Address Font Size (px)')}
										name={`${focusIdx}.attributes.title-font-size`}
										config={pixelAdapter}
									/>
								</Grid.Col>
							</>}

							{/* Listing Price Section */}
							<Grid.Col span={24}>
								<SwitchField
									label={
										<span style={{ fontWeight: 400, display: 'inline-block' }}>
											{t('Show Price')}
										</span>
									}
									name={`${focusIdx}.attributes.show-price`}
									size='small'
									formItem={{
										className: 'm-0',
										style: {
											display: 'flex',
											flexDirection: 'row',
											alignItems: 'center',
											marginBottom: '0px',
										},
										labelCol: {
											span: 0,
											style: {
												order: 2,
												paddingLeft: '12px',
												paddingRight: 0,
												width: 'auto',
											},
										},
										wrapperCol: {
											span: 0,
											style: {
												order: 1,
											},
										},
									}}
								/>
							</Grid.Col>
							{showPriceInput.value && <>
								<Grid.Col span={12} className='mb-3'>
									<ColorPickerField
										formItem={{ className: 'm-0' }}
										label={t('Listing Price Color')}
										name={`${focusIdx}.attributes.listing-price-color`}
									/>
								</Grid.Col>
								<Grid.Col span={12} className='mb-3'>
									<InputWithUnitField
										formItem={{ className: 'm-0' }}
										label={t('Price Font Size (px)')}
										name={`${focusIdx}.attributes.price-font-size`}
										config={pixelAdapter}
									/>
								</Grid.Col>
							</>}
							
							{/* Listing Details Section */}
							<Grid.Col span={24}>
								<SwitchField
									label={
										<span style={{ fontWeight: 400, display: 'inline-block' }}>
											{t('Show Details')}
										</span>
									}
									name={`${focusIdx}.attributes.show-details`}
									size='small'
									formItem={{
										className: 'm-0',
										style: {
											display: 'flex',
											flexDirection: 'row',
											alignItems: 'center',
											marginBottom: '0px',
										},
										labelCol: {
											span: 0,
											style: {
												order: 2,
												paddingLeft: '12px',
												paddingRight: 0,
												width: 'auto',
											},
										},
										wrapperCol: {
											span: 0,
											style: {
												order: 1,
											},
										},
									}}
								/>
							</Grid.Col>
							{showDetailsInput.value && <>
								<Grid.Col span={12} className='mb-3'>
									<ColorPickerField
										formItem={{ className: 'm-0' }}
										label={t('Listing Details Color')}
										name={`${focusIdx}.attributes.listing-address-color`}
									/>
								</Grid.Col>
								<Grid.Col span={12} className='mb-3'>
									<InputWithUnitField
										formItem={{ className: 'm-0' }}
										label={t('Details Font Size (px)')}
										name={`${focusIdx}.attributes.address-font-size`}
										config={pixelAdapter}
									/>
								</Grid.Col>
							</>}
							
							{/* Button Section */}
							<Grid.Col span={12} className='mb-3'>
								<ColorPickerField
									formItem={{ className: 'm-0' }}
									label={t('Button Color')}
									name={`${focusIdx}.attributes.button-color`}
								/>
							</Grid.Col>
							<Grid.Col span={12} className='mb-3'>
								<ColorPickerField
									formItem={{ className: 'm-0' }}
									label={t('Button Text Color')}
									name={`${focusIdx}.attributes.button-text-color`}
								/>
							</Grid.Col>
							<Grid.Col span={12}>
								<InputWithUnitField
									formItem={{ className: 'm-0' }}
									label={t('Button Font Size (px)')}
									name={`${focusIdx}.attributes.button-font-size`}
									config={pixelAdapter}
								/>
							</Grid.Col>
						 </Grid.Row>
					</div>
				</Collapse.Item>
			</Collapse>
		</AttributesPanelWrapper>
	);
};
